/**
 * Onde o balão cabe, em relação ao elemento destacado.
 *
 * ## Por que isto é nosso e não de uma biblioteca
 *
 * Medido em 21/10: a CSP desta aplicação (`style-src 'self'`) recusa o
 * **atributo** `style=` e o elemento `<style>`, mas **aceita** atribuição por
 * CSSOM. Isso liberou as bibliotecas de tour, que posicionam justamente assim —
 * e aí o que restou a favor de escrevê-lo aqui foi o tamanho do problema:
 * pôr uma caixa perto de um retângulo, virar de lado se não couber, e prender
 * na janela. É este arquivo inteiro.
 *
 * Ver `documentation/TOUR-GUIADO.md` §5 para a medição e o que reabre a
 * decisão.
 *
 * ## O contrato com o CSS
 *
 * Esta função devolve **números**. Quem aplica escreve propriedades
 * customizadas via CSSOM, e a folha estática faz o resto:
 *
 *     raiz.style.setProperty('--tour-x', `${x}px`)   // passa pela CSP
 *     .tour-balao { left: var(--tour-x); }           // a folha é arquivo
 *
 * Nenhuma decisão visual atravessa essa fronteira — só coordenada.
 */

/** Respiro entre o elemento destacado e o balão. */
const FOLGA = 12;

/** Quanto o balão nunca encosta na borda da janela. */
const MARGEM = 16;

/**
 * Prende um valor entre dois limites.
 *
 * `Math.min` antes de `Math.max` **de propósito**: numa janela mais estreita
 * que o balão os dois limites se invertem, e esta ordem faz o balão encostar
 * na margem esquerda em vez de sair pela direita. A ordem contrária o jogaria
 * para fora justamente no caso em que já está apertado.
 */
const prende = (valor, min, max) => Math.max(min, Math.min(valor, max));

/**
 * Os lados possíveis, na ordem em que são tentados.
 *
 * Abaixo primeiro porque é onde a leitura continua natural: o olho vem do
 * elemento e segue para o texto. Acima é a segunda melhor. Os lados só entram
 * quando o elemento ocupa a altura toda — tabela larga, barra lateral.
 */
const LADOS = ['abaixo', 'acima', 'direita', 'esquerda'];

/** O balão cabe deste lado do alvo, sem passar por cima dele? */
function cabe(lado, alvo, balao, janela) {
  if (lado === 'abaixo') return alvo.bottom + FOLGA + balao.altura + MARGEM <= janela.altura;
  if (lado === 'acima') return alvo.top - FOLGA - balao.altura - MARGEM >= 0;
  if (lado === 'direita') return alvo.right + FOLGA + balao.largura + MARGEM <= janela.largura;
  return alvo.left - FOLGA - balao.largura - MARGEM >= 0;
}

/** A coordenada do balão para um lado já escolhido. */
function coloca(lado, alvo, balao, janela) {
  const centroX = alvo.left + alvo.largura / 2 - balao.largura / 2;
  const centroY = alvo.top + alvo.altura / 2 - balao.altura / 2;

  const limiteX = [MARGEM, Math.max(MARGEM, janela.largura - balao.largura - MARGEM)];
  const limiteY = [MARGEM, Math.max(MARGEM, janela.altura - balao.altura - MARGEM)];

  if (lado === 'abaixo') {
    return { x: prende(centroX, ...limiteX), y: alvo.bottom + FOLGA };
  }
  if (lado === 'acima') {
    return { x: prende(centroX, ...limiteX), y: alvo.top - FOLGA - balao.altura };
  }
  if (lado === 'direita') {
    return { x: alvo.right + FOLGA, y: prende(centroY, ...limiteY) };
  }
  return { x: alvo.left - FOLGA - balao.largura, y: prende(centroY, ...limiteY) };
}

/**
 * Onde desenhar o balão.
 *
 * @param {DOMRect|null} alvo retângulo do elemento destacado; nulo centraliza
 * @param {{largura: number, altura: number}} balao medido depois de desenhado
 * @param {{largura: number, altura: number}} janela
 * @param {string} [preferido] lado pedido pelo passo, tentado antes dos outros
 * @returns {{x: number, y: number, lado: string}}
 */
export function calcular(alvo, balao, janela, preferido) {
  // Passo sem âncora é passo de abertura ou de fecho: fala da plataforma, não
  // de um canto dela. O lugar certo é o meio da tela.
  if (!alvo) {
    return {
      x: Math.max(MARGEM, janela.largura / 2 - balao.largura / 2),
      y: Math.max(MARGEM, janela.altura / 2 - balao.altura / 2),
      lado: 'centro',
    };
  }

  const ordem = preferido ? [preferido, ...LADOS.filter((l) => l !== preferido)] : LADOS;
  const escolhido = ordem.find((lado) => cabe(lado, alvo, balao, janela));

  // Nenhum lado coube: a janela é pequena demais, ou o alvo ocupa quase tudo.
  // Cair para `abaixo` prenderia o balão na margem de baixo, por cima do alvo.
  // O meio da tela é honesto — o destaque continua mostrando de onde se fala.
  if (!escolhido) {
    return {
      x: Math.max(MARGEM, janela.largura / 2 - balao.largura / 2),
      y: Math.max(MARGEM, janela.altura / 2 - balao.altura / 2),
      lado: 'centro',
    };
  }

  return { ...coloca(escolhido, alvo, balao, janela), lado: escolhido };
}

/**
 * O retângulo de um elemento, em coordenadas da janela.
 *
 * Devolve nulo quando o elemento não existe **ou não ocupa espaço**. A segunda
 * metade importa tanto quanto a primeira: um elemento `display: none` responde
 * `getBoundingClientRect` com zeros, e o balão apontaria para o canto superior
 * esquerdo com toda a convicção.
 */
export function retanguloDe(elemento) {
  if (!elemento) return null;
  const r = elemento.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;

  return {
    top: r.top,
    left: r.left,
    right: r.right,
    bottom: r.bottom,
    largura: r.width,
    altura: r.height,
  };
}

export default { calcular, retanguloDe };
